import { readPublicConfig } from "../config";
import { readAdminSession } from "./adminRepository";

export type WorkspaceVariant = {
  id: string;
  offerId: string;
  slug: string;
  label: string | null;
  durationMinutes: number;
  priceCents: number | null;
  currency: string;
  sortOrder: number;
  isDefault: boolean;
  isActive: boolean;
};

export type WorkspaceOffer = {
  id: string;
  slug: string;
  title: string;
  eyebrow: string | null;
  description: string | null;
  setting: string | null;
  accent: "navy" | "gold" | "sage" | "stone";
  sortOrder: number;
  isActive: boolean;
  variants: WorkspaceVariant[];
};

export type PersonalBooking = {
  id: string;
  reference: string;
  status: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  note: string;
  startsAt: string;
  endsAt: string;
  resourceSlug: string;
  resourceName: string;
  offerTitle: string;
  variantLabel: string;
  durationMinutes: number | null;
  locationName: string;
  locationMode: string;
  priceCents: number | null;
  currency: string;
  paymentChoice: string;
  paymentStatus: string;
  paymentProvider: string;
  paymentMethod: string;
  paidAt: string | null;
  createdAt: string;
};

export type WorkspaceSnapshot = {
  source: "live";
  adminEmail: string;
  bookings: PersonalBooking[];
  offers: WorkspaceOffer[];
  locations: Array<{ id: string; name: string; mode: "online" | "in_person"; isActive: boolean }>;
  offerLocations: Array<{ offerId: string; locationId: string; isActive: boolean }>;
  windows: Array<{
    id: string;
    startsAt: string;
    endsAt: string;
    isActive: boolean;
    openSlotCount: number;
  }>;
  recentChanges: Array<{
    id: string;
    actorType: string;
    actor: string;
    action: string;
    entityType: string;
    createdAt: string;
  }>;
};

export type HourlyPricingInput = {
  offerId: string;
  hourlyRateCents: number | null;
  durationMinutes: number[];
  defaultDurationMinutes: number;
  currency: string;
};

const OFFERS_ENDPOINT =
  "https://cnejticdknokawqlslsj.supabase.co/functions/v1/booking-admin-offers";

export async function loadWorkspaceSnapshot(): Promise<WorkspaceSnapshot> {
  const session = await requireSession();
  const response = await fetch(readPublicConfig(import.meta.env).bookingAdminApiUrl, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  return readResponse<WorkspaceSnapshot>(response);
}

export async function markPersonalBookingPaid(bookingId: string): Promise<void> {
  await bookingAdminCommand({ action: "mark_booking_paid", bookingId });
}

export async function markPersonalBookingUnpaid(bookingId: string): Promise<void> {
  await bookingAdminCommand({ action: "mark_booking_unpaid", bookingId });
}

async function bookingAdminCommand(body: Record<string, unknown>): Promise<void> {
  const session = await requireSession();
  const response = await fetch(readPublicConfig(import.meta.env).bookingAdminApiUrl, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  await readResponse(response);
}

export async function saveWorkspaceOffer(
  offer: Omit<WorkspaceOffer, "id" | "slug" | "variants"> & { id?: string },
): Promise<string> {
  const result = await command<{ id: string }>({ action: "save_offer", offer });
  return result.id;
}

export async function saveWorkspaceVariant(variant: Omit<WorkspaceVariant, "slug">): Promise<void> {
  await command({ action: "save_variant", variant });
}

export async function saveWorkspaceHourlyPricing(pricing: HourlyPricingInput): Promise<void> {
  await command({ action: "save_hourly_pricing", pricing });
}

async function command<T = unknown>(body: Record<string, unknown>): Promise<T> {
  const session = await requireSession();
  const response = await fetch(OFFERS_ENDPOINT, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return readResponse<T>(response);
}

async function requireSession() {
  const session = await readAdminSession();
  if (!session) throw new Error("Die Anmeldung ist abgelaufen. Bitte erneut anmelden.");
  return session;
}

async function readResponse<T = unknown>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(errorMessage(payload.error));
  return payload;
}

function errorMessage(error?: string): string {
  switch (error) {
    case "offer_title_required":
      return "Bitte einen Titel für das Angebot eintragen.";
    case "variant_label_required":
      return "Bitte eine Bezeichnung für die Dauer eintragen.";
    case "invalid_variant_duration":
    case "invalid_hourly_duration":
      return "Die Dauer muss zwischen 15 Minuten und 8 Stunden liegen und in 15-Minuten-Schritten gewählt werden.";
    case "invalid_variant_price":
    case "invalid_hourly_rate":
      return "Der Stundensatz darf nicht negativ sein.";
    case "hourly_duration_required":
      return "Bitte mindestens eine buchbare Dauer auswählen.";
    case "default_duration_not_selected":
      return "Die vorausgewählte Dauer muss auch buchbar sein.";
    case "authentication_required":
      return "Die Anmeldung ist abgelaufen. Bitte erneut anmelden.";
    default:
      return "Die Änderung konnte gerade nicht gespeichert werden.";
  }
}
